import React from 'react';
import CalendarHeatmap from 'react-calendar-heatmap';
import styled from 'styled-components';

import { Scalars, useUserStatsQuery } from '../hooks/query';
import { toDateString } from '../util';

const today = new Date();
const prevYear = new Date().setDate(today.getDate() - 182);

const Content = styled.div`
  height: 118px;

  & .react-calendar-heatmap .color-scale-1 {
    fill: #7e97e2;
  }
  & .react-calendar-heatmap .color-scale-2 {
    fill: #5072d7;
  }
  & .react-calendar-heatmap .color-scale-3 {
    fill: #2d54c8;
  }
  & .react-calendar-heatmap .color-scale-4 {
    fill: #23419a;
  }
`;

type TooltipProps = {
  visible: boolean;
  x: number;
  y: number;
};

const Tooltip = styled.div<TooltipProps>`
  position: absolute;
  z-index: 10;
  background-color: rgba(0, 0, 0, 0.8);
  color: white;
  padding: 5px 10px;
  border-radius: 4px;
  font-size: 12px;
  pointer-events: none;

  opacity: ${props => props.visible ? 1 : 0};
  left: ${props => props.x}px;
  top: ${props => props.y - 40}px;
`;

type Value = { date: string; count: number};
type Values = Value[];

const aggregate = (values: Values): Values => {
  const agg = values.reduce<{ [K: string]: number }>(
    (prev, obj) => ({
      ...prev,
      [obj.date]: obj.date in prev ? prev[obj.date] + obj.count : obj.count,
    }),
    {}
  );
  return Object.entries(agg).map(([date, count]) => ({ date, count }));
};

export const UserCalendarHeatmap: React.FC<{ userId: Scalars['ID'] }> = ({ userId }) => {
  const { loading, error, data } = useUserStatsQuery({ variables: { id: userId } });
  const [values, setValues] = React.useState<Values>([]);
  const [tooltip, setTooltip] = React.useState({ show: false, content: '', x: 0, y: 0 });

  React.useEffect(() => {
    if (!loading && !error && data?.user) {
      const favs = data.user.favorites.edge.map((i) => ({
        date: toDateString(i.createdAt),
        count: 1,
      }));
      const readLaters = data.user.readLaterPapers.edge.map((i) => ({
        date: toDateString(i.changedAt),
        count: 1,
      }));
      const comments = data.user.comments.edge.map((i) => ({
        date: toDateString(i.createdAt),
        count: 3,
      }));
      const reads = data.user.readPapers.edge.map((i) => ({
        date: toDateString(i.changedAt),
        count: 5,
      }));
      const newValues = aggregate([...favs, ...readLaters, ...comments, ...reads]);
      setValues(newValues);
    }
  }, [loading, error, data]);

  const getDaySuffix = (day: number): string => {
    if (day < 1 || day > 31) return '';
    if (day >= 11 && day <= 13) return 'th';
    const lastDigit = day % 10;
    switch (lastDigit) {
      case 1:
        return 'st';
      case 2:
        return 'nd';
      case 3:
        return 'rd';
      default:
        return 'th';
    }
  }

  const getGitHubStyleTooltipContent = (value: Value): string => {
    if (!value) return '';
    
    const date = value.date;
    const MONTHS = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    const parts = date.split('-');
    const month = MONTHS[parseInt(parts[1]) - 1];
    const day : number = parseInt(parts[2]);
    const daySuffix = getDaySuffix(day);
    const formattedDate = `${month} ${day}${daySuffix}`;
    
    // contribution表示
    const contributionText = value.count === 1 
      ? '1 contribution' // 単数形
      : `${value.count} contributions`; // 複数形
    
    return `${contributionText} on ${formattedDate}`;
  };

  // ツールチップを表示する関数
  const showTooltip = (e: React.MouseEvent<SVGRectElement>, value: Value) => {
    if (!value) {
      setTooltip({ show: false, content: '', x: 0, y: 0 });
      return;
    }
    const target = e.target as HTMLElement;
    const rect = target.getBoundingClientRect();
    const content = getGitHubStyleTooltipContent(value);
    setTooltip({
      show: true,
      content,
      x: rect.left + window.scrollX,
      y: rect.top + window.scrollY
    });
  };

  // ツールチップを非表示にする関数
  const hideTooltip = () => {
    setTooltip({ ...tooltip, show: false });
  };
  return (
    <Content>
      <CalendarHeatmap
        startDate={prevYear}
        endDate={today}
        classForValue={(value): string => {
          if (!value) {
            return 'color-empty';
          }
          if (value.count > 20) return 'color-scale-4';
          if (value.count > 15) return 'color-scale-3';
          if (value.count > 7) return 'color-scale-2';
          return 'color-scale-1';
        }}
        values={values}
        onMouseOver={(e, value) => showTooltip(e, value)}
        onMouseLeave={hideTooltip}
      />
      <Tooltip 
        visible={tooltip.show} 
        x={tooltip.x} 
        y={tooltip.y}
      >
        {tooltip.content}
      </Tooltip>
    </Content>
  );
};
